import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useModalOpen } from '../../app/components/polaris';
import agent from '../../app/api/agent';
import type { ProductFitmentCopyJob, ProductVariantFitmentScope } from '../../app/models/Product';

const RECENT_COPY_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const HISTORY_PAGE_SIZE = 25;
type ActivityProps = {
    jobs: ProductFitmentCopyJob[];
    variants?: ProductVariantFitmentScope[];
    disabled: boolean;
    onRetry: (job: ProductFitmentCopyJob) => Promise<ProductFitmentCopyJob | undefined>;
};

export function isRecentCopyJob(job: ProductFitmentCopyJob, now = Date.now()) {
    if (job.isActive || ['Queued', 'Processing', 'Failed', 'CompletedWithWarnings'].includes(job.state)) return true;
    const timestamp = Date.parse(job.completedAt || job.requestedAt);
    // A missing/invalid timestamp is not evidence that a result has expired.
    return !Number.isFinite(timestamp) || now - timestamp < RECENT_COPY_AGE_MS;
}

const formatTimestamp = (value: string) => Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'Date unavailable';

const relativeTimestamp = (value: string) => {
    if (!Number.isFinite(Date.parse(value))) return 'Date unavailable';
    const date = new Date(value);
    const days = Math.max(0, Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000)));
    const day = days === 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days} days ago`;
    return `${day} · ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

function CopyActivityEntry({ job, variants = [], disabled, onRetry, absoluteDate = false }: Omit<ActivityProps, 'jobs'> & {
    job: ProductFitmentCopyJob; absoluteDate?: boolean;
}) {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const tipId = `pf-copy-entry-${job.id}`;
    const completed = job.state === 'Completed';
    const timestamp = job.completedAt || job.requestedAt;
    const source = job.sourceProductTitle || `Product ${job.sourceProductId}`;
    const variant = variants.find((item) => item.id === job.targetVariantId);
    const scope = job.targetVariantId ? `Variant: ${variant?.sku || variant?.title || job.targetVariantId}` : 'Product-level fitments';
    const tone = completed ? 'success' : job.state === 'Failed' ? 'critical' : job.state === 'CompletedWithWarnings' ? 'caution' : job.isActive ? 'info' : 'auto';
    const stateLabel = job.state === 'CompletedWithWarnings' ? 'Completed with warnings' : job.state === 'Processing' ? 'Copying' : job.state;
    const result = completed ? `${job.totalFitments.toLocaleString()} ${job.totalFitments === 1 ? 'fitment' : 'fitments'} copied`
        : job.state === 'Failed' ? 'Copy needs attention' : job.state === 'CompletedWithWarnings' ? 'Copy completed with warnings'
            : job.state === 'Queued' ? 'Copy queued' : job.state === 'Cancelled' ? 'Copy cancelled' : 'Copying fitments';
    const details: [string, string][] = !detailsOpen ? [] : [
        ['Source product', source],
        ['Destination scope', scope],
        ...(completed ? [['Fitments copied', job.totalFitments.toLocaleString()] as [string, string]] : []),
        ['Added', job.addedFitments.toLocaleString()],
        ['Already present', job.unchangedFitments.toLocaleString()],
        ...(job.removedFitments > 0 ? [['Removed', job.removedFitments.toLocaleString()] as [string, string]] : []),
        ['Notes copied', `${job.copiedNotes.toLocaleString()}${!job.includeNotes ? ' (not included)' : ''}`],
        ['Started', formatTimestamp(job.startedAt || job.requestedAt)],
        ...(job.completedAt ? [['Finished', formatTimestamp(job.completedAt)] as [string, string]] : []),
    ];

    return (
        <s-box padding="base" accessibilityLabel={`${stateLabel} copy from ${source}`}>
            <s-grid gridTemplateColumns="auto 1fr" gap="small" alignItems="start">
                <s-icon type={completed ? 'check-circle' : 'duplicate'} tone={completed ? 'success' : 'auto'} color={completed ? 'base' : 'subdued'} />
                <s-stack gap="small-300">
                    <s-stack direction="inline" gap="small-200" alignItems="center">
                        <s-heading>{result}</s-heading>
                        <s-badge tone={tone}>{stateLabel}</s-badge>
                    </s-stack>
                    <s-text color="subdued">From {source}</s-text>
                    {!completed && job.message && <s-text color="subdued">{job.message}</s-text>}
                    <s-stack direction="inline" gap="small-200" alignItems="center">
                        <s-text color="subdued" interestFor={`${tipId}-time`}>
                            {absoluteDate ? formatTimestamp(timestamp) : relativeTimestamp(timestamp)}
                        </s-text>
                        <s-tooltip id={`${tipId}-time`}>{formatTimestamp(timestamp)}</s-tooltip>
                        {job.targetVariantId && <s-text color="subdued">{scope}</s-text>}
                        <s-button variant="tertiary" icon={detailsOpen ? 'chevron-up' : 'chevron-down'} onClick={() => setDetailsOpen((open) => !open)}>
                            {detailsOpen ? 'Hide details' : 'Show details'}
                        </s-button>
                        {job.state === 'Failed' && <s-button variant="tertiary" disabled={disabled} onClick={() => { void onRetry(job); }}>Retry copy</s-button>}
                    </s-stack>
                    {detailsOpen && (
                        <s-grid gridTemplateColumns="auto 1fr" columnGap="base" rowGap="small-300">
                            {details.map(([label, value]) => (
                                <Fragment key={label}>
                                    <s-text color="subdued">{label}</s-text>
                                    <s-text>{value}</s-text>
                                </Fragment>
                            ))}
                        </s-grid>
                    )}
                </s-stack>
            </s-grid>
        </s-box>
    );
}

export function RecentCopyActivity({ jobs, onViewAll, ...props }: ActivityProps & { onViewAll: () => void }) {
    const [expanded, setExpanded] = useState(false);
    const recent = jobs.filter((job) => isRecentCopyJob(job));
    if (!recent.length) return null;
    return (
        <s-section heading="Recent copy activity" padding={expanded ? 'none' : 'base'}>
            <s-badge slot="accessory">{String(recent.length)}</s-badge>
            <s-button slot="secondary-actions" variant="tertiary" icon={expanded ? 'chevron-up' : 'chevron-down'}
                accessibilityLabel={expanded ? 'Hide recent copy activity' : 'Show recent copy activity'}
                onClick={() => setExpanded((open) => !open)} />
            <s-button slot="secondary-actions" variant="tertiary" onClick={onViewAll}>View all</s-button>
            {expanded && (
                <s-stack gap="none">
                    {recent.map((job, index) => <Fragment key={job.id}>
                        {index > 0 && <s-divider />}
                        <CopyActivityEntry job={job} {...props} />
                    </Fragment>)}
                </s-stack>
            )}
        </s-section>
    );
}

export function CopyHistoryModal({ productId, open, onClose, jobs, onRetry, ...props }: ActivityProps & {
    productId: number; open: boolean; onClose: () => void;
}) {
    const [history, setHistory] = useState<ProductFitmentCopyJob[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasMore, setHasMore] = useState(false);
    const abortRef = useRef<AbortController | undefined>(undefined);
    const lastCursor = useRef<number | undefined>(undefined);
    const load = useCallback(async (beforeId?: number) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        lastCursor.current = beforeId;
        setLoading(true);
        setError('');
        try {
            const page = await agent.Products.listFitmentCopyJobs(productId, HISTORY_PAGE_SIZE, { beforeId, signal: controller.signal });
            if (controller.signal.aborted) return;
            setHistory((current) => beforeId
                ? [...current, ...page.filter((job) => !current.some((existing) => existing.id === job.id))] : page);
            setHasMore(page.length === HISTORY_PAGE_SIZE);
        } catch {
            if (!controller.signal.aborted) setError('Copy history could not be loaded. Try again.');
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        if (!open) return;
        setHistory([]);
        setHasMore(false);
        void load();
        return () => abortRef.current?.abort();
    }, [open, load]);

    const retry = async (job: ProductFitmentCopyJob) => {
        const retried = await onRetry(job);
        if (retried) setHistory((current) => current.map((item) => item.id === job.id ? retried : item));
        else setError('The copy could not be restarted. Try again.');
        return retried;
    };
    // Polling keeps already loaded rows live without discarding older pages.
    const currentJobs = new Map(jobs.map((job) => [job.id, job]));
    const modalRef = useModalOpen(open, onClose);
    return (
        <s-modal ref={modalRef} id="pf-copy-history-modal" heading="Copy history" padding="none">
            <s-stack gap="none">
                {error && (
                    <s-box padding="base">
                        <s-banner tone="critical" heading={error}>
                            <s-button slot="secondary-actions" onClick={() => { void load(lastCursor.current); }}>Reload history</s-button>
                        </s-banner>
                    </s-box>
                )}
                {!history.length && !loading && !error && <s-box padding="base"><s-text color="subdued">No copy history for this product.</s-text></s-box>}
                {history.map((job, index) => <Fragment key={job.id}>
                    {index > 0 && <s-divider />}
                    <CopyActivityEntry job={currentJobs.get(job.id) ?? job} absoluteDate onRetry={retry} {...props} />
                </Fragment>)}
                {loading && (
                    <s-stack direction="inline" gap="small-200" alignItems="center" padding="base" accessibilityRole="status">
                        <s-spinner size="base" accessibilityLabel="Loading copy history" />
                        <s-text>Loading copy history…</s-text>
                    </s-stack>
                )}
            </s-stack>
            <s-button slot="primary-action" variant="primary" onClick={onClose}>Done</s-button>
            {hasMore && !error && (
                <s-button slot="secondary-actions" loading={loading} disabled={loading}
                    onClick={() => { void load(history[history.length - 1]?.id); }}>
                    Load older
                </s-button>
            )}
        </s-modal>
    );
}
